import { Formik, FormikErrors } from 'formik';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { useAppTranslation } from '../../services/i18n';
import { useAppDispatch } from '../../store';
import { createUserLabel, updateUserLabel } from '../../store/userLabels';
import { addMessage } from '../../store/localApp';
import { Button, Grid, IconButton, LinearProgress, Tooltip } from '@mui/material';
import { Check } from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import Paper from '@mui/material/Paper';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { TextFormField } from '../form/TextFormField';
import { isApiResultError } from '../../helpers/api';
import { UserLabel, UserLabelColorEnum } from '../../generated-api';
import { USER_LABEL_COLORS, USER_LABEL_NO_COLOR, USER_LABEL_PICKER_ORDER } from './UserLabelColorSwatch';

type UserLabelSaveFormFields = {
  name: string;
  color: UserLabelColorEnum;
};

const validate = (values: UserLabelSaveFormFields, t: any) => {
  let errors = {} as FormikErrors<UserLabelSaveFormFields>;
  if (!values.name || !values.name.trim()) {
    errors.name = t('userLabels.form.error.nameRequired');
  }
  return errors;
};

const UserLabelColorPicker = ({ value, onChange }: {
  value: UserLabelColorEnum;
  onChange: (color: UserLabelColorEnum) => void;
}) => {
  const t = useAppTranslation();
  return (
    <div>
      {USER_LABEL_PICKER_ORDER.map((color) => {
        const isSelected = color === value;
        const isNoColor = color === USER_LABEL_NO_COLOR;
        return (
          <Tooltip key={color} title={isNoColor ? t('userLabels.form.noColor') : undefined}>
            <IconButton onClick={() => onChange(color)}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  borderRadius: '4px',
                  backgroundColor: USER_LABEL_COLORS[color],
                  border: isNoColor ? '1px solid #D0D0D0' : undefined,
                  boxSizing: 'border-box',
                }}
              >
                {isSelected && <Check style={{ color: isNoColor ? '#424242' : '#fff', fontSize: 18 }} />}
              </span>
            </IconButton>
          </Tooltip>
        );
      })}
    </div>
  );
};

type Props = {
  item?: UserLabel | null;
  onSuccess: () => unknown;
  onClose: () => unknown;
};

export const UserLabelSaveForm = (props: Props) => {
  const t = useAppTranslation();
  const dispatch = useAppDispatch();
  const [isSaving, setIsSaving] = useState(false);

  const { item, onSuccess, onClose } = props;

  const handleSaveItem = useCallback(
    async (values: UserLabelSaveFormFields) => {
      const body = { name: values.name, color: values.color };

      setIsSaving(true);
      const result = item
        ? await dispatch(updateUserLabel({ id: '' + item.id, body }))
        : await dispatch(createUserLabel({ body }));
      setIsSaving(false);

      if (!isApiResultError(result)) {
        dispatch(
          addMessage({
            severity: 'success',
            title: item
              ? t('userLabels.form.success.updated')
              : t('userLabels.form.success.created'),
          })
        );
        onSuccess();
        onClose();
      }
    },
    [dispatch, item, onSuccess, onClose, t]
  );

  return (
    <Dialog open={true} onClose={props.onClose} PaperComponent={Paper}>
      <Formik
        initialValues={
          {
            name: item?.name || '',
            color: item?.color || USER_LABEL_NO_COLOR,
          } as UserLabelSaveFormFields
        }
        onSubmit={handleSaveItem}
        validate={(v) => validate(v, t)}
      >
        {(formProps) => (
          <form onSubmit={formProps.handleSubmit}>
            <DialogTitle>
              {item ? t('userLabels.editOne') : t('userLabels.addOne')}
            </DialogTitle>
            <LinearProgress hidden={!isSaving} />
            <DialogContent>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextFormField
                    name="name"
                    label={t('userLabels.form.nameTitle')}
                    maxlength={100}
                  />
                </Grid>
                <Grid size={12}>
                  <p>{t('userLabels.form.colorTitle')}</p>
                  <UserLabelColorPicker
                    value={formProps.values.color}
                    onChange={(color) => formProps.setFieldValue('color', color)}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button color={'inherit'} onClick={onClose} disabled={isSaving}>
                {t('form.cancel')}
              </Button>
              <Button color={'primary'} variant={'contained'} type={'submit'} disabled={isSaving}>
                {item ? t('form.update') : t('form.add')}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};
